import { useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import { Button, PageShell, Pagination, Skeleton } from '@/components/ui';
import { ApiClientError } from '@/lib/http-core';
import type { AdminRole } from '@/lib/api-admin/types';
import {
  SETTLEMENT_SORT_VALUES,
  useSettlementFiltersStore,
  type SettlementSort,
} from '@/stores/useSettlementFiltersStore';
import { useAuthContext } from '@/providers/AuthProvider';
import { canGenerateSettlement } from '../lib/settlementActionRbac';
import { SettlementFiltersBar } from '../components/SettlementFiltersBar';
import { SettlementRunsTable } from '../components/SettlementRunsTable';
import { GenerateCycleModal } from '../components/GenerateCycleModal';
import { useSettlementsQuery } from '../hooks/useSettlementsQuery';
import { useGenerateSettlementCycleMutation } from '../hooks/useSettlementMutation';

const isSettlementSort = (s: string): s is SettlementSort =>
  SETTLEMENT_SORT_VALUES.includes(s as SettlementSort);

const errorMessageOf = (err: unknown): string | null => {
  if (!err) return null;
  if (err instanceof ApiClientError) return err.message ?? err.code;
  if (err instanceof Error) return err.message;
  return null;
};

export const SettlementListPage = () => {
  const { t } = useTranslation('settlement');
  const { currentAdmin } = useAuthContext();
  const roles = (currentAdmin?.roles ?? []) as ReadonlyArray<AdminRole>;
  const canGenerate = canGenerateSettlement(roles);

  const vendorId = useSettlementFiltersStore((s) => s.vendorId);
  const cycle = useSettlementFiltersStore((s) => s.cycle);
  const runType = useSettlementFiltersStore((s) => s.runType);
  const status = useSettlementFiltersStore((s) => s.status);
  const periodFrom = useSettlementFiltersStore((s) => s.periodFrom);
  const periodTo = useSettlementFiltersStore((s) => s.periodTo);
  const page = useSettlementFiltersStore((s) => s.page);
  const size = useSettlementFiltersStore((s) => s.size);
  const sort = useSettlementFiltersStore((s) => s.sort);
  const setSort = useSettlementFiltersStore((s) => s.setSort);
  const setPage = useSettlementFiltersStore((s) => s.setPage);

  const query = useSettlementsQuery({
    page,
    size,
    sort,
    vendorId,
    cycle,
    runType,
    status,
    periodFrom,
    periodTo,
  });
  const generate = useGenerateSettlementCycleMutation();
  const [showGenerate, setShowGenerate] = useState(false);

  const items = query.data?.items ?? [];
  const totalItems = query.data?.meta.pagination?.totalItems ?? 0;
  const generateError = errorMessageOf(generate.error);

  return (
    <PageShell
      title={
        <span className="flex flex-col gap-0.5">
          <span id="settlement-list-heading">{t('settlement.list.title')}</span>
          <span className="text-xs font-normal text-[color:var(--content-muted)]">
            {t('settlement.list.subject')}
          </span>
        </span>
      }
      actions={
        canGenerate ? (
          <Button
            type="button"
            variant="cta"
            onClick={() => setShowGenerate(true)}
            data-testid="generate-cycle-trigger"
          >
            {t('settlement.actions.generate')}
          </Button>
        ) : undefined
      }
      aria-labelledby="settlement-list-heading"
    >
      <SettlementFiltersBar />

      {query.isPending ? (
        <div role="status" aria-live="polite" className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-[44px] w-full" />
          ))}
        </div>
      ) : query.isError ? (
        <div
          role="alert"
          aria-live="polite"
          className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
          style={{
            background: 'var(--status-danger-bg)',
            color: 'var(--status-danger-fg)',
          }}
        >
          <p>{t('settlement.list.error')}</p>
          <Button variant="outline" size="sm" onClick={() => query.refetch()}>
            {t('settlement.actions.retry')}
          </Button>
        </div>
      ) : (
        <SettlementRunsTable
          items={items}
          sort={sort}
          onSortChange={(next) => {
            if (isSettlementSort(next)) setSort(next);
          }}
        />
      )}

      <Pagination
        total={totalItems}
        currentPage={page}
        pageSize={size}
        onPageChange={setPage}
      />

      <GenerateCycleModal
        isOpen={showGenerate}
        isSubmitting={generate.isPending}
        errorMessage={generateError}
        onCancel={() => setShowGenerate(false)}
        onSubmit={({ cycleId, body }) =>
          generate.mutate(
            { cycleId, body },
            { onSuccess: () => setShowGenerate(false) },
          )
        }
      />
    </PageShell>
  );
};
